// Settings service for managing app configuration
import { SettingsApiResponse, SettingsCache, DEFAULT_SETTINGS, SettingKey, AdminPermissions } from '../types/settings';

class SettingsService {
  private cache: SettingsCache | null = null;
  private readonly API_BASE = '';
  private readonly PUBLIC_CACHE_DURATION = 10 * 60 * 1000; // 10 minutes
  private readonly ADMIN_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get public settings with caching
   */
  async getPublicSettings(): Promise<Record<string, any>> {
    try {
      // Check cache first
      if (this.isValidCache(this.PUBLIC_CACHE_DURATION)) {
        return this.cache!.data;
      }

      const response = await fetch(`${this.API_BASE}/api/settings?scope=public`);
      const data: SettingsApiResponse = await response.json();

      if (!data.success || !data.settings) {
        console.warn('Failed to fetch public settings, using defaults');
        return this.getDefaultSettings();
      }

      // Extract values from the settings object
      const settingsValues: Record<string, any> = {};
      Object.keys(data.settings).forEach(key => {
        settingsValues[key] = data.settings![key].value;
      });

      // Update cache
      this.updateCache(settingsValues);

      return settingsValues;
    } catch (error) {
      console.error('Error fetching public settings:', error);
      return this.getDefaultSettings();
    }
  }

  /**
   * Get all settings (admin only)
   */
  async getAllSettings(fid: string): Promise<Record<string, any>> {
    try {
      if (this.isValidCache(this.ADMIN_CACHE_DURATION)) {
        return this.cache!.data;
      }

      const response = await fetch(`${this.API_BASE}/api/settings?fid=${fid}`);
      
      // Fallback: If API returns error, use defaults
      if (!response.ok) {
        console.warn(`Settings API returned ${response.status} (${response.statusText}), using defaults`);
        console.log('💡 This is expected - using fallback settings for admin panel');
        return this.getFlatDefaultSettings();
      }
      
      const data: SettingsApiResponse = await response.json();

      if (!data.success || !data.settings) {
        console.warn('Failed to fetch admin settings');
        return this.getFlatDefaultSettings();
      }

      const settingsValues: Record<string, any> = {};
      Object.keys(data.settings).forEach(key => {
        settingsValues[key] = data.settings![key].value;
      });

      this.updateCache(data.settings); // Store full structure
      return settingsValues; // Return flat values, not structured data
    } catch (error) {
      console.error('Error fetching admin settings:', error);
      return this.getFlatDefaultSettings();
    }
  }

  /**
   * Get a specific setting value
   */
  async getSettingValue<T = any>(key: SettingKey, defaultValue?: T, fid?: string): Promise<T> {
    try {
      // Try to get from cache first
      if (this.cache && this.cache.data[key] !== undefined) {
        return this.cache.data[key] as T;
      }

      // Determine if we need admin access
      const isPublicSetting = this.isPublicSetting(key);
      
      let settings: Record<string, any>;
      if (isPublicSetting) {
        settings = await this.getPublicSettings();
      } else if (fid) {
        settings = await this.getAllSettings(fid);
      } else {
        // Fallback to default
        return (defaultValue !== undefined ? defaultValue : DEFAULT_SETTINGS[key]) as T;
      }

      const value = settings[key];
      return value !== undefined ? value as T : (defaultValue !== undefined ? defaultValue : DEFAULT_SETTINGS[key]) as T;
    } catch (error) {
      console.error(`Error getting setting ${key}:`, error);
      return (defaultValue !== undefined ? defaultValue : DEFAULT_SETTINGS[key]) as T;
    }
  }

  /**
   * Update a setting (admin only)
   */
  async updateSetting(key: string, value: any, fid: string, description?: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.API_BASE}/api/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          key,
          value,
          fid,
          description,
        }),
      });

      const data: SettingsApiResponse = await response.json();

      if (data.success) {
        // Clear cache to force refresh
        this.clearCache();
        return true;
      }

      console.error('Failed to update setting:', data.error);
      return false;
    } catch (error) {
      console.error('Error updating setting:', error);
      return false;
    }
  }

  /**
   * Check if user is admin
   */
  async isAdminUser(fid: string): Promise<boolean> {
    try {
      const adminUsers = await this.getSettingValue('admin_users', [], fid);
      return Array.isArray(adminUsers) && adminUsers.includes(parseInt(fid));
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }

  /**
   * Check if user is super admin
   */
  async isSuperAdminUser(fid: string): Promise<boolean> {
    try {
      const superAdminUsers = await this.getSettingValue('super_admin_users', [], fid);
      return Array.isArray(superAdminUsers) && superAdminUsers.includes(parseInt(fid));
    } catch (error) {
      console.error('Error checking super admin status:', error);
      return false;
    }
  }

  /**
   * Get admin permissions for a user
   */
  async getAdminPermissions(fid: string): Promise<AdminPermissions> {
    try {
      const [isAdmin, isSuperAdmin] = await Promise.all([
        this.isAdminUser(fid),
        this.isSuperAdminUser(fid),
      ]);

      return {
        isAdmin,
        isSuperAdmin,
        canUpdateSettings: isAdmin || isSuperAdmin,
        canAccessDebugMode: isAdmin || isSuperAdmin,
      };
    } catch (error) {
      console.error('Error getting admin permissions:', error);
      return {
        isAdmin: false,
        isSuperAdmin: false,
        canUpdateSettings: false,
        canAccessDebugMode: false,
      };
    }
  }

  /**
   * Check if app is in maintenance mode
   */
  async isMaintenanceMode(): Promise<boolean> {
    return await this.getSettingValue('maintenance_mode', false);
  }

  /**
   * Get reading limits for a user
   */
  async getReadingLimits() {
    const [dailyLimit, additionalLimit, maxTotal] = await Promise.all([
      this.getSettingValue('daily_reading_limit', 1),
      this.getSettingValue('additional_reading_limit', 2),
      this.getSettingValue('max_total_daily_readings', 3),
    ]);

    return {
      dailyLimit: parseInt(dailyLimit),
      additionalLimit: parseInt(additionalLimit),
      maxTotal: parseInt(maxTotal),
    };
  }

  /**
   * Clear the settings cache
   */
  clearCache(): void {
    this.cache = null;
  }

  /**
   * Force refresh settings from server
   */
  async refreshSettings(fid?: string): Promise<Record<string, any>> {
    this.clearCache();
    return fid ? await this.getAllSettings(fid) : await this.getPublicSettings();
  }

  // Private helper methods
  private isValidCache(maxAge: number): boolean {
    if (!this.cache) return false;
    
    const now = Date.now();
    return (now - this.cache.timestamp) < maxAge;
  }

  private updateCache(data: Record<string, any>): void {
    this.cache = {
      data,
      timestamp: Date.now(),
      duration: this.PUBLIC_CACHE_DURATION,
    };
  }

  private getDefaultSettings(): Record<string, any> {
    // Temporary: Return structured data like API would return
    const defaults = { ...DEFAULT_SETTINGS };
    const structured: Record<string, any> = {};
    
    Object.keys(defaults).forEach(key => {
      structured[key] = {
        value: defaults[key as keyof typeof defaults],
        description: this.getSettingDescription(key),
        category: this.getSettingCategory(key),
        data_type: this.getSettingDataType(key),
        updated_at: new Date().toISOString()
      };
    });
    
    return structured;
  }

  /**
   * Get flat defaults (values only) for getAllSettings fallback
   */
  private getFlatDefaultSettings(): Record<string, any> {
    console.log('🔧 Using flat fallback defaults with admin_users:', DEFAULT_SETTINGS.admin_users);
    return { ...DEFAULT_SETTINGS };
  }

  private getSettingDescription(key: string): string {
    const descriptions: Record<string, string> = {
      'daily_reading_limit': 'Default daily readings per user',
      'additional_reading_limit': 'Default additional readings per user', 
      'max_total_daily_readings': 'Maximum total readings per day per user',
      'admin_users': 'FIDs with admin access and reset button',
      'super_admin_users': 'FIDs with full admin privileges',
      'debug_mode': 'Enable debug logging and test features',
      'streak_enabled': 'Enable streak tracking system',
      'reset_button_enabled': 'Show reset button for admin users',
      'maintenance_mode': 'Enable maintenance mode (disable app)',
      'nft_required_for_additional': 'Require NFT ownership for additional readings',
      'streak_reset_hour': 'UTC hour when streaks reset (0-23)',
      'app_title': 'Application title',
      'app_subtitle': 'Application subtitle',
      'max_question_length': 'Maximum characters allowed in question field',
      'cache_settings_duration': 'Settings cache duration in seconds',
      'max_api_requests_per_minute': 'Rate limit for API requests per user'
    };
    return descriptions[key] || 'Application setting';
  }

  private getSettingCategory(key: string): string {
    if (['daily_reading_limit', 'additional_reading_limit', 'max_total_daily_readings'].includes(key)) return 'limits';
    if (['admin_users', 'super_admin_users', 'debug_mode'].includes(key)) return 'admin';
    if (['streak_enabled', 'reset_button_enabled', 'maintenance_mode', 'nft_required_for_additional', 'streak_reset_hour'].includes(key)) return 'features';
    if (['app_title', 'app_subtitle', 'max_question_length'].includes(key)) return 'ui';
    if (['cache_settings_duration', 'max_api_requests_per_minute'].includes(key)) return 'performance';
    return 'other';
  }

  private getSettingDataType(key: string): string {
    const value = DEFAULT_SETTINGS[key as keyof typeof DEFAULT_SETTINGS];
    if (typeof value === 'string') return 'string';
    if (typeof value === 'number') return 'number';
    if (typeof value === 'boolean') return 'boolean';
    if (Array.isArray(value)) return 'array';
    return 'object';
  }

  private isPublicSetting(key: SettingKey): boolean {
    // These settings are typically marked as public in the database
    const publicSettings = [
      'daily_reading_limit',
      'additional_reading_limit',
      'max_total_daily_readings',
      'streak_enabled',
      'reset_button_enabled',
      'maintenance_mode',
      'nft_required_for_additional',
      'streak_reset_hour',
      'app_title',
      'app_subtitle',
      'max_question_length',
    ];
    
    return publicSettings.includes(key);
  }
}

// Export singleton instance
export const settingsService = new SettingsService();

// Export utility functions
export const getSettingValue = <T = any>(key: SettingKey, defaultValue?: T, fid?: string): Promise<T> => {
  return settingsService.getSettingValue(key, defaultValue, fid);
};

export const isAdminUser = (fid: string): Promise<boolean> => {
  return settingsService.isAdminUser(fid);
};

export const isSuperAdminUser = (fid: string): Promise<boolean> => {
  return settingsService.isSuperAdminUser(fid);
};

export const getAdminPermissions = (fid: string): Promise<AdminPermissions> => {
  return settingsService.getAdminPermissions(fid);
};

export const isMaintenanceMode = (): Promise<boolean> => {
  return settingsService.isMaintenanceMode();
};
